import { Badge } from './ui/badge';

/**
 * <EngineBanner>
 * ─────────────────────────────────────────────────────────────────
 * Banner informativo reutilizable para reportar señales que emite el
 * motor financiero (truncamiento de flujos, absorción de sobrecostos,
 * versión desactualizada, etc.). El componente NO calcula nada — solo
 * pinta. Toda la información viene del backend (Engine v2.6.0).
 *
 * Props:
 *   - severity: 'info' | 'success' | 'warning' | 'critical'
 *   - title: string · encabezado del banner
 *   - tag: string · etiqueta a la derecha del título (ej. "Check 7")
 *   - amount: string · monto destacado a la derecha del tag (opcional)
 *   - children: contenido del cuerpo (descripción, grid de cifras, etc.)
 *   - actions: ReactNode opcional alineado a la derecha del header
 *   - testid: string · data-testid raíz
 */

const PALETTE = {
  info: {
    border: 'var(--info, #3B82F6)',
    bg: 'var(--info-soft, #EFF6FF)',
    text: '#1E3A5F',
    iconBg: 'var(--info, #3B82F6)',
    icon: 'i',
  },
  success: {
    border: 'var(--success-color, #10B981)',
    bg: 'var(--success-soft, #ECFDF5)',
    text: '#065F46',
    iconBg: 'var(--success-color, #10B981)',
    icon: '✓',
  },
  warning: {
    border: 'var(--warning-color, #F5A623)',
    bg: 'var(--warning-soft, #FEF3C7)',
    text: '#78350F',
    iconBg: 'var(--warning-color, #F5A623)',
    icon: '!',
  },
  critical: {
    border: 'var(--danger, #DF1B41)',
    bg: 'var(--danger-soft, #FEE2E2)',
    text: '#7F1D1D',
    iconBg: 'var(--danger, #DF1B41)',
    icon: '!',
  },
};

export function EngineBanner({
  severity = 'info',
  title,
  tag,
  amount,
  children,
  actions,
  testid,
}) {
  const p = PALETTE[severity] || PALETTE.info;
  return (
    <div
      className="rounded-xl border p-4"
      data-testid={testid}
      style={{ borderColor: `${p.border}40`, background: p.bg }}
    >
      <div className="flex gap-3">
        <div
          className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-bold"
          style={{ background: p.iconBg }}
        >
          {p.icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            {title && (
              <span
                className="text-sm font-display font-semibold"
                style={{ color: p.text }}
                data-testid={testid ? `${testid}-title` : undefined}
              >
                {title}
              </span>
            )}
            {tag && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 py-0"
                style={{ borderColor: `${p.border}60`, color: p.text }}
              >
                {tag}
              </Badge>
            )}
            {amount && (
              <span
                className="text-xs font-display font-bold tabular-nums"
                style={{ color: p.text }}
                data-testid={testid ? `${testid}-amount` : undefined}
              >
                {amount}
              </span>
            )}
            {actions && <div className="ml-auto">{actions}</div>}
          </div>
          {children && (
            <div
              className="text-xs font-medium"
              style={{ color: `${p.text}CC` }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EngineBanner;
